import { PlusIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';

import { Button, Card, Spinner } from '@realvisor/ui';
import { useLeads, useDefaultPipeline, usePipelines, useUpdateLead, type Lead } from '@/hooks';
import { getLeadAgeColor, formatLeadAge } from '@/utils/leadAgeColor';
import { useProject } from '@/contexts/ProjectContext';
import CreateLeadModal from '@/components/leads/CreateLeadModal';

// Helper to get project info for pipeline cards
function ProjectIndicatorSmall({ project }: { project: { logoUrl?: string | null; color?: string; name: string } }) {
  if (project.logoUrl && project.logoUrl.startsWith('http')) {
    return (
      <img 
        src={project.logoUrl} 
        alt={project.name}
        className="h-4 w-4 rounded object-contain"
      />
    );
  }
  return (
    <span 
      className="h-2 w-2 rounded" 
      style={{ backgroundColor: project.color || '#3B82F6' }} 
    />
  );
}

function getInitials(firstName: string, lastName?: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

function formatValue(value: number | null): string {
  if (!value) return '-';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M Kč`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K Kč`;
  return `${value.toLocaleString()} Kč`;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Dnes';
    if (diffDays === 1) return 'Včera';
    if (diffDays < 7) return `Před ${diffDays} dny`;
    if (diffDays < 30) return `Před ${Math.floor(diffDays / 7)} týdny`;
    
    return date.toLocaleDateString('cs-CZ');
  } catch (error) {
    console.error('Invalid date format:', dateString, error);
    return '-';
  }
}

// Draggable Lead Card Component
function DraggableLeadCard({ lead, isDragging, projectColor, showProject }: { lead: Lead; isDragging?: boolean; projectColor?: string; showProject?: boolean }) {
  const navigate = useNavigate();
  const ageInfo = getLeadAgeColor(lead.createdAt, lead.lastTouchAt);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  const handleClick = (e: React.MouseEvent) => {
    // Only navigate if not dragging
    if (!isDragging && !isSortableDragging) {
      navigate(`/leads/${lead.id}`);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className="cursor-grab active:cursor-grabbing"
    >
      <Card padding="sm" className="hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-700">
            {getInitials(lead.contact.firstName, lead.contact.lastName)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-gray-900">
              {lead.contact.firstName} {lead.contact.lastName}
            </p>
            <p className="truncate text-xs text-gray-500">
              {lead.contact.email || lead.contact.phone || 'Bez kontaktu'}
            </p>
          </div>
          {/* Pulzující tečka + čas */}
          <div className="flex items-center gap-1.5">
            <span 
              className={`h-5 w-5 rounded-full ${ageInfo.isUrgent ? 'animate-pulse' : ''}`}
              style={{ backgroundColor: ageInfo.indicatorColor }}
              title={ageInfo.label}
            />
            <span 
              className="text-xs text-gray-500"
              title={`Stáří: ${formatLeadAge(lead.createdAt)}`}
            >
              {formatLeadAge(lead.createdAt)}
            </span>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900">
            {formatValue(Number(lead.expectedValue))}
          </span>
        </div>
        {showProject && lead.project && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <span 
              className="inline-flex items-center gap-1.5 text-xs"
              style={{ color: projectColor }}
            >
              <ProjectIndicatorSmall project={lead.project} />
              {lead.project.name}
            </span>
          </div>
        )}
      </Card>
    </div>
  );
}

// Droppable Column Component
function DroppableColumn({ 
  stageId, 
  children, 
  isEmpty 
}: { 
  stageId: string; 
  children: React.ReactNode;
  isEmpty: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: stageId,
  });

  return (
    <div 
      ref={setNodeRef}
      className={`space-y-3 min-h-[100px] p-2 -m-2 rounded-lg transition-colors ${
        isOver ? 'bg-primary-50 ring-2 ring-primary-200' : ''
      }`}
    >
      {children}
      {isEmpty && (
        <div className={`rounded-lg border-2 border-dashed p-4 text-center text-sm transition-colors ${
          isOver ? 'border-primary-300 text-primary-500' : 'border-gray-200 text-gray-400'
        }`}>
          {isOver ? 'Pusťte zde' : 'Přetáhněte sem lead'}
        </div>
      )}
    </div>
  );
}

// Lead Card for Drag Overlay (visual feedback while dragging)
function LeadCardOverlay({ lead }: { lead: Lead }) {
  const ageInfo = getLeadAgeColor(lead.createdAt, lead.lastTouchAt);
  return (
    <Card padding="sm" className="shadow-xl bg-white rotate-3 w-72">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-medium text-primary-700">
          {getInitials(lead.contact.firstName, lead.contact.lastName)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">
            {lead.contact.firstName} {lead.contact.lastName}
          </p>
          <p className="truncate text-xs text-gray-500">
            {lead.contact.email || lead.contact.phone || 'Bez kontaktu'}
          </p>
        </div>
        <span 
          className={`h-5 w-5 rounded-full ${ageInfo.isUrgent ? 'animate-pulse' : ''}`}
          style={{ backgroundColor: ageInfo.indicatorColor }}
        />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">
          {formatValue(Number(lead.expectedValue))}
        </span>
        <span className="text-xs text-gray-500">
          {formatDate(lead.createdAt)}
        </span>
      </div>
    </Card>
  );
}

export default function Pipeline() {
  const navigate = useNavigate();
  const { currentProject, isAllProjects, getProjectColor } = useProject();
  
  // Při "Všechny projekty" načíst všechny pipeline, jinak jen default pro projekt
  const { data: allPipelines, isLoading: allPipelinesLoading } = usePipelines();
  const { data: defaultPipeline, isLoading: defaultPipelineLoading } = useDefaultPipeline(
    isAllProjects ? undefined : currentProject?.id
  );
  
  const { data: leadsData, isLoading: leadsLoading, refetch } = useLeads({ 
    limit: 100,
    projectId: isAllProjects ? undefined : currentProject?.id,
  });
  const updateLead = useUpdateLead();
  
  // Použít buď všechny pipeline (při Všechny projekty) nebo jen default
  const pipeline = isAllProjects ? null : defaultPipeline;
  const pipelineLoading = isAllProjects ? allPipelinesLoading : defaultPipelineLoading;
  
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [hideOwnLeads, setHideOwnLeads] = useState(() => {
    const saved = localStorage.getItem('hideOwnLeads');
    return saved ? JSON.parse(saved) : true; // Defaultně aktivní
  });

  const allLeads = leadsData?.data || [];
  
  // Filtrovat vlastní leady (David Choc - contact ID)
  const DAVID_CHOC_CONTACT_ID = 'b291e5c9-f51f-482d-bb0c-3d4a271e465c';
  const leads = hideOwnLeads 
    ? allLeads.filter(lead => lead.contactId !== DAVID_CHOC_CONTACT_ID)
    : allLeads;
  
  // Při "Všechny projekty" sloučit fáze ze všech pipeline podle stageType
  const stages = useMemo(() => {
    if (isAllProjects && allPipelines) {
      // Seskupit fáze podle stageType
      const stagesByType = new Map<string, any>();
      
      allPipelines.forEach(p => {
        p.stages.forEach(stage => {
          const type = stage.stageType || stage.id;
          if (!stagesByType.has(type)) {
            stagesByType.set(type, {
              id: type, // Použít stageType jako ID
              name: stage.name,
              color: stage.color,
              orderIndex: stage.orderIndex,
              probability: stage.probability,
              isCloseWon: stage.isCloseWon,
              isCloseLost: stage.isCloseLost,
              stageType: stage.stageType,
              stageIds: [stage.id], // Seznam všech stage ID s tímto typem
            });
          } else {
            // Přidat další stage ID ke stejnému typu
            stagesByType.get(type)!.stageIds.push(stage.id);
          }
        });
      });
      
      return Array.from(stagesByType.values()).sort((a, b) => a.orderIndex - b.orderIndex);
    }
    
    return pipeline?.stages || [];
  }, [isAllProjects, allPipelines, pipeline]);

  // Group leads by stage
  const leadsByStage = useMemo(() => {
    const grouped: Record<string, Lead[]> = {};
    stages.forEach(stage => {
      grouped[stage.id] = [];
    });
    
    leads.forEach(lead => {
      const stageId = lead.currentStageId;
      
      if (isAllProjects) {
        // Při "Všechny projekty" najít správnou skupinu podle stageIds
        const matchingStage = stages.find(stage => 
          stage.stageIds && stage.stageIds.includes(stageId)
        );
        if (matchingStage) {
          const group = grouped[matchingStage.id];
          if (group) {
            group.push(lead);
          }
        }
      } else {
        // Při konkrétním projektu použít přímé mapování
        if (stageId && grouped[stageId]) {
          grouped[stageId].push(lead);
        }
      }
    });
    
    return grouped;
  }, [leads, stages, isAllProjects]);

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const leadId = active.id as string;
    const overId = over.id as string;

    // Find which stage the lead was dropped on
    // overId can be either a stage ID or another lead ID
    let targetStageId: string | null = null;

    // Check if dropped on a stage
    const droppedOnStage = stages.find(s => s.id === overId);
    if (droppedOnStage) {
      targetStageId = droppedOnStage.id;
    } else {
      // Dropped on another lead - find that lead's stage
      const targetLead = leads.find(l => l.id === overId);
      if (targetLead) {
        targetStageId = targetLead.currentStageId;
      }
    }

    if (!targetStageId) return;

    // Find current lead
    const currentLead = leads.find(l => l.id === leadId);
    if (!currentLead || currentLead.currentStageId === targetStageId) return;

    // Find stage name for toast
    const targetStage = stages.find(s => s.id === targetStageId);

    try {
      await updateLead.mutateAsync({
        id: leadId,
        data: { stageId: targetStageId },
      });
      
      toast.success(`Lead přesunut do "${targetStage?.name}"`);
      refetch();
    } catch (error) {
      toast.error('Nepodařilo se přesunout lead');
      console.error('Failed to update lead stage:', error);
    }
  };

  const isLoading = pipelineLoading || leadsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // Při "Všechny projekty" kontrolovat stages, jinak pipeline
  const hasNoPipeline = isAllProjects 
    ? (!allPipelines || allPipelines.length === 0)
    : !pipeline;

  if (hasNoPipeline) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <h3 className="text-sm font-medium text-gray-900">Žádná pipeline</h3>
          <p className="mt-1 text-sm text-gray-500">Vytvořte první pipeline pro zobrazení kanban pohledu.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline</h1>
          <p className="mt-1 text-sm text-gray-500">
            {isAllProjects ? 'Všechny projekty' : pipeline?.name} • {leads.length} leadů {hideOwnLeads && `(${allLeads.length - leads.length} skryto)`} • Přetáhněte karty mezi sloupci
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hideOwnLeads}
              onChange={(e) => {
                const value = e.target.checked;
                setHideOwnLeads(value);
                localStorage.setItem('hideOwnLeads', JSON.stringify(value));
              }}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <EyeSlashIcon className="h-4 w-4 text-gray-400" />
            <span>Skrýt vlastní leady</span>
          </label>
          <Button 
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Nový lead
          </Button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => {
            const stageLeads = leadsByStage[stage.id] || [];
            const stageValue = stageLeads.reduce((sum, l) => sum + (Number(l.expectedValue) || 0), 0);
            
            return (
              <div key={stage.id} className="flex-shrink-0 w-72">
                <div className="flex items-center gap-2 mb-3">
                  <div 
                    className="h-3 w-3 rounded-full" 
                    style={{ backgroundColor: stage.color }}
                  />
                  <h3 className="font-medium text-gray-900">{stage.name}</h3>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {stageLeads.length}
                  </span>
                </div>
                
                {stageValue > 0 && (
                  <p className="text-xs text-gray-500 mb-3">
                    {formatValue(stageValue)}
                  </p>
                )}

                <SortableContext
                  id={stage.id}
                  items={stageLeads.map(l => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableColumn stageId={stage.id} isEmpty={stageLeads.length === 0}>
                    {stageLeads.map((lead) => (
                      <DraggableLeadCard 
                        key={lead.id} 
                        lead={lead} 
                        isDragging={activeId === lead.id}
                        showProject={isAllProjects}
                        projectColor={getProjectColor(lead.projectId)}
                      />
                    ))}
                  </DroppableColumn>
                </SortableContext>

                <button className="w-full mt-3 rounded-lg border-2 border-dashed border-gray-200 p-3 text-sm text-gray-500 hover:border-gray-300 hover:text-gray-600">
                  + Přidat lead
                </button>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {activeLead ? <LeadCardOverlay lead={activeLead} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Create Lead Modal */}
      <CreateLeadModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(leadId) => {
          refetch();
          navigate(`/leads/${leadId}`);
        }}
      />
    </div>
  );
}
